import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// useSectionHighlight — the on-page "over here" mechanism (Phase 4a, guided
// go-live checklist — replaces the modal wizard). A checklist click needs to
// do more than switch tabs: it has to scroll the right section into a
// CONSISTENT focal position, put a brief attention pulse on it, and
// auto-focus its first empty input — Lisa's exact complaint about the old
// modal was that a plain tab-switch/scroll wasn't enough to tell the operator
// where to actually act.
//
// Shape: a section registers itself under a stable string id via
// `registerSection(id)` (a ref-callback). `highlightNow(id)` scrolls that
// section to ~1/3 from the viewport top, flags it as highlighted for
// `PULSE_MS` (index.css's `.section-pulse` renders the fade), and focuses the
// first empty input/select/textarea inside it. Sections that mount
// asynchronously (behind a query, or behind a tab switch that hasn't
// committed yet) are handled by a short retry loop rather than requiring the
// caller to sequence things perfectly.
//
// Extension point for Pass 2 (the spark flourish): pass `onHighlight` to
// useSectionHighlightController — it fires with (id, element) every time a
// highlight actually lands on a real DOM node. Pass 2 hooks its particle
// effect there instead of re-deriving scroll/focus timing.
// ─────────────────────────────────────────────────────────────────────────────

export type HighlightTarget = {
  /** Section id to highlight. */
  sectionId: string;
};

export type SectionHighlightApi = {
  /** Ref callback — attach to a section's root element under a stable id. */
  registerSection: (id: string) => (el: HTMLElement | null) => void;
  /** True while `id`'s pulse ring should be rendered (drive a class off this). */
  isHighlighted: (id: string) => boolean;
  /** Scroll + pulse + auto-focus section `id` now. Retries briefly if the
   * section hasn't mounted yet (e.g. a tab switch or query is still landing). */
  highlightNow: (id: string) => void;
};

const SectionHighlightContext = createContext<SectionHighlightApi | null>(null);

const RETRY_MS = 90;
const MAX_RETRIES = 12; // ~1s total — covers a tab switch + a query round-trip
const PULSE_MS = 1500;

export function useSectionHighlightController(
  onHighlight?: (id: string, el: HTMLElement) => void,
): SectionHighlightApi {
  const refs = useRef(new Map<string, HTMLElement>());
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const clearTimer = useRef<number>();
  const retryTimer = useRef<number>();

  const registerSection = useCallback((id: string) => (el: HTMLElement | null) => {
    if (el) refs.current.set(id, el);
    else refs.current.delete(id);
  }, []);

  const isHighlighted = useCallback((id: string) => highlightId === id, [highlightId]);

  const highlightNow = useCallback((id: string) => {
    window.clearTimeout(retryTimer.current);
    let attempts = 0;
    const tryIt = () => {
      const el = refs.current.get(id);
      if (el) {
        const rect = el.getBoundingClientRect();
        // Consistent focal position — ~1/3 from the top, not a tiny nudge.
        const focalY = window.scrollY + rect.top - window.innerHeight / 3;
        window.scrollTo({ top: Math.max(0, focalY), behavior: 'smooth' });

        setHighlightId(id);
        window.clearTimeout(clearTimer.current);
        clearTimer.current = window.setTimeout(() => {
          setHighlightId(cur => (cur === id ? null : cur));
        }, PULSE_MS);

        const fields = Array.from(
          el.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea'),
        ).filter(f => !f.disabled && f.type !== 'hidden');
        const empty = fields.find(f => !f.value);
        empty?.focus({ preventScroll: true });

        onHighlight?.(id, el);
      } else if (attempts < MAX_RETRIES) {
        attempts += 1;
        retryTimer.current = window.setTimeout(tryIt, RETRY_MS);
      }
      // Past MAX_RETRIES, give up quietly — e.g. a founder-only section that
      // never mounts for the current role. The tab switch already happened;
      // losing the pulse is a soft failure, not a crash.
    };
    tryIt();
  }, [onHighlight]);

  return useMemo(
    () => ({ registerSection, isHighlighted, highlightNow }),
    [registerSection, isHighlighted, highlightNow],
  );
}

export function SectionHighlightProvider({
  value,
  children,
}: {
  value: SectionHighlightApi;
  children: ReactNode;
}) {
  return <SectionHighlightContext.Provider value={value}>{children}</SectionHighlightContext.Provider>;
}

/** Returns null outside a provider — callers (e.g. GuideMeLauncher mounted on
 * a page other than Setup) fall back to plain cross-page navigation. */
export function useSectionHighlightContext(): SectionHighlightApi | null {
  return useContext(SectionHighlightContext);
}
